/**
 * External dependencies
 */
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Internal dependencies
 */
import Tiptap from '@Components/TipTap';
import AiSnippetPickerModal from '../AiSnippetPickerModal';
import { UpdateSpacePayload } from '@Types/api';
import { Space } from '@Types/venue';
import service from '../../service';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';

const FormSchema = z.object({
  name: z.string().min(2, {
    message: 'A name is required.',
  }),
  // normalize description so empty strings become undefined
  description: z
    .string()
    .optional()
    .transform((val) => (val && val.trim().length ? val.trim() : undefined)),
  teaser: z.string().optional().nullable(),
  capacity: z
    .number()
    .min(1, {
      message: 'Please enter the number of guests',
    })
    .max(1000, {
      message: 'Max capacity is 1000',
    }),
  guestMin: z
    .number()
    .min(1, {
      message: 'Please enter the number of guests',
    })
    .max(1000, {
      message: 'Max guest minimum is 1000',
    }),
  tripleSeatId: z.number().int().positive().nullable(),
  enabled: z.boolean(),
});

type FormValues = z.infer<typeof FormSchema>;

export interface EditSpaceSheetProps extends React.PropsWithChildren {
  space: Space;
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
}

export default function EditSpaceSheet({
  space,
  isOpen,
  setIsOpen,
}: EditSpaceSheetProps) {
  const queryClient = useQueryClient();
  const [snippetModalOpen, setSnippetModalOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: space.name,
      description: space.description ?? '',
      teaser: space.teaser ?? '',
      capacity: space.capacity,
      guestMin: space.guestMin,
      tripleSeatId: space.tripleSeatId ?? null,
      enabled: space.disabledAt ? false : true,
    },
  });

  // Keep form in sync if space changes while sheet is open
  useEffect(() => {
    if (isOpen) {
      form.reset({
        name: space.name,
        description: space.description ?? '',
        teaser: space.teaser ?? '',
        capacity: space.capacity,
        guestMin: space.guestMin,
        tripleSeatId: space.tripleSeatId ?? null,
        enabled: space.disabledAt ? false : true,
      });
    }
  }, [space, isOpen, form]);

  const onSubmit = async (values: FormValues) => {
    const {
      name,
      description,
      teaser,
      capacity,
      guestMin,
      tripleSeatId,
      enabled,
    } = values;

    const body: UpdateSpacePayload = {
      name,
      ...(typeof description !== 'undefined' ? { description } : {}),
      ...(typeof teaser !== 'undefined' && teaser !== null
        ? { teaser: teaser.trim() }
        : {}),
      capacity,
      guestMin,
      tripleSeatId: tripleSeatId ?? null,
      disabledAt: enabled ? null : new Date().toISOString(),
    };

    // clear any previous root error
    form.clearErrors('root');

    try {
      await service.updateSpace(space.id, body);

      await queryClient.invalidateQueries({ queryKey: ['space', space.id] });
      await queryClient.invalidateQueries({ queryKey: ['spaces'] });

      setIsOpen(false);
      toastSuccess('Space updated successfully');
    } catch (error) {
      console.error('Error updating space:', error);
      form.setError('root', {
        type: 'server',
        message:
          'We were unable to update this space. Please check your changes and try again.',
      });
    }
  };

  const handleOnOpenChange = (newIsSheetOpen: boolean) => {
    setIsOpen(newIsSheetOpen);

    if (!newIsSheetOpen) {
      form.reset({
        name: space.name,
        description: space.description ?? '',
        teaser: space.teaser ?? '',
        capacity: space.capacity,
        guestMin: space.guestMin,
        tripleSeatId: space.tripleSeatId ?? null,
        enabled: space.disabledAt ? false : true,
      });
    }
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
      <SheetContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
            <div className="flex h-full flex-col">
              <SheetHeader>
                <SheetTitle>{`Edit ${space.name}'s details`}</SheetTitle>
              </SheetHeader>

              <SheetBody className="grow overflow-auto">
                {/* Top-level error banner */}
                {rootError && (
                  <div className="mb-4">
                    <Alert variant="destructive">
                      <AlertTitle>Something went wrong</AlertTitle>
                      <AlertDescription>{rootError}</AlertDescription>
                    </Alert>
                  </div>
                )}

                <div className="space-y-6 py-3">
                  {/* Name */}
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Space name</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. The Lounge" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Description */}
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between">
                          <FormLabel>Description</FormLabel>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setSnippetModalOpen(true)}
                          >
                            Insert AI Snippet
                          </Button>
                        </div>
                        <FormControl>
                          <Tiptap
                            value={field.value || ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder="Describe this space..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <AiSnippetPickerModal
                    open={snippetModalOpen}
                    onOpenChange={setSnippetModalOpen}
                    onInsert={(tag) => {
                      const current = form.getValues('description') ?? '';
                      const sep =
                        current &&
                        !current.endsWith(' ') &&
                        !current.endsWith('>')
                          ? ' '
                          : '';
                      form.setValue('description', `${current}${sep}${tag}`, {
                        shouldDirty: true,
                      });
                    }}
                  />

                  {/* Teaser */}
                  <FormField
                    control={form.control}
                    name="teaser"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Teaser</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Short summary or tagline for this space."
                            {...field}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Capacity */}
                  <FormField
                    control={form.control}
                    name="capacity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Capacity</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="00"
                            type="number"
                            {...field}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Guest Minimum */}
                  <FormField
                    control={form.control}
                    name="guestMin"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Guest Minimum</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="00"
                            type="number"
                            {...field}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Tripleseat Room ID */}
                  <FormField
                    control={form.control}
                    name="tripleSeatId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tripleseat Room ID</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === ''
                                  ? null
                                  : Number(e.target.value),
                              )
                            }
                          />
                        </FormControl>
                        <FormDescription>
                          Numeric Room ID from Tripleseat used for event
                          bookings.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator
                    className="my-8 h-px bg-zinc-200"
                    orientation="horizontal"
                  />

                  {/* Enabled */}
                  <FormField
                    control={form.control}
                    name="enabled"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) =>
                                field.onChange(checked === true)
                              }
                            />
                          </FormControl>
                          <FormLabel>Enabled</FormLabel>
                        </div>
                        <FormDescription>
                          Disabling a space hides it from users.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </SheetBody>

              <SheetFooter>
                <Button
                  type="submit"
                  variant="primary"
                  loading={form.formState.isSubmitting}
                >
                  Update
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOnOpenChange(false)}
                >
                  Cancel
                </Button>
              </SheetFooter>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
